import { Column, Columns, FlexBox, Spinner } from '@dixa/lib-shared-habla';
import { SearchResultEnriched } from 'lib-search-types';
import { useEffect } from 'react';

import {
  ConversationContextProvider,
  ConversationSummaryByCsid,
} from 'components/conversation';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { ConversationLegacyPreviewHeader } from './components/header/conversationLegacyPreviewHeader';
import { ConversationLegacyPreviewMessages } from './components/messages/conversationLegacyPreviewMessages';

/** @deprecated Use `ConversationPreview` instead. */
type ConversationLegacyPreviewProps = {
  csid: number;
  highlights: SearchResultEnriched<'conversations'>['highlights'];
  onGoToConversation: () => void;
  onPreviewClose: () => void;
  /**
   * Whether to show internal/agent-facing details: internal notes, tags and
   * custom attributes. Defaults to true.
   */
  showInternalDetails?: boolean;
  /** Whether to show the header go-to/close actions. Defaults to true. */
  showActions?: boolean;
  /** Whether to show the customer/end-user contact details. Defaults to false. */
  showCustomer?: boolean;
  /** Whether to show each message's email recipients. Defaults to false. */
  showRecipients?: boolean;
  /** Whether to show the AI summary. Defaults to true. */
  showSummary?: boolean;
};

/**
 * A deprecated component that renders a conversation preview in legacy UI, relying on Redux
 * `metaConversation` and other legacy concepts. It should not be used for any new features.
 *
 * Instead, the `ConversationPreview` component should be used for standalone conversation previews,
 * or dispatch the `app:conversationPreview` modal action for a modal conversation preview.
 *
 *
 * @deprecated Use `ConversationPreview` instead. */
export const ConversationLegacyPreview = ({
  csid,
  highlights,
  onGoToConversation,
  onPreviewClose,
  showInternalDetails = true,
  showActions = true,
  showCustomer = false,
  showRecipients = false,
  showSummary = true,
}: ConversationLegacyPreviewProps): JSX.Element => {
  const dispatch = useAppDispatch();

  const { metaConversation, asyncStatus } = useAppSelector((state) => ({
    metaConversation: state.conversation.metaConversation,
    asyncStatus: state.conversation.asyncStatus.getConversation,
  }));

  const conversation = metaConversation.conversation;

  // We continue to use the legacy API here due to the fact that the component tree rendered below
  // relies on some legacy components to display conversation information. Until we'll have time to
  // completely overhaul the UI and rewrite components, we won't be using the new RTK Query APIs.
  useEffect(() => {
    dispatch(
      conversationActions.conversationGet({
        csid,
        skipStoreAndWs: true,
      }),
    );
  }, [dispatch, csid]);

  const isLoading = asyncStatus === 'loading';

  return (
    <Columns>
      <Column>
        <Column.Row noMinHeight noHorizontalPadding noVerticalPadding>
          <ConversationLegacyPreviewHeader
            csid={csid}
            metaConversation={metaConversation}
            highlights={highlights}
            onPreviewClose={onPreviewClose}
            onGoToConversation={onGoToConversation}
            showActions={showActions}
            showDetails={showInternalDetails}
            showCustomer={showCustomer}
          />
        </Column.Row>

        <Column.Row size={1} scrollable>
          {isLoading ? (
            <FlexBox
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <Spinner size={18} />
            </FlexBox>
          ) : (
            <FlexBox flexDirection="column" gap="200">
              {showSummary &&
                conversation.id &&
                conversation.summary?.can_be_generated && (
                  <ConversationContextProvider conversation={conversation}>
                    <ConversationSummaryByCsid
                      csid={conversation.id}
                      view="searchView"
                    />
                  </ConversationContextProvider>
                )}

              <ConversationLegacyPreviewMessages
                messages={metaConversation.messages}
                requesterId={conversation.requested_by}
                showNotes={showInternalDetails}
                showRecipients={showRecipients}
              />
            </FlexBox>
          )}
        </Column.Row>
      </Column>
    </Columns>
  );
};
